import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { useGetTodayMeetingsQuery } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { MeetingStatus } from '../../types';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const ReceptionistDashboardScreen = ({ navigation }: any) => {
  const { data: meetings = [], isLoading, refetch } = useGetTodayMeetingsQuery();
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const pendingCount = meetings.filter((m) => m.status === MeetingStatus.PENDING).length;

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Reception Hub" subtitle="Create & Track Visitor Meeting Requests" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 24 }}
        refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
      >
        {/* Quick Action Button */}
        <TouchableOpacity
          onPress={() => navigation.navigate('CreateMeeting')}
          style={styles.actionBanner}
        >
          <View>
            <Text style={styles.actionTitle}>➕ Create New Meeting</Text>
            <Text style={styles.actionSubtitle}>Book slot & submit for Master Admin approval</Text>
          </View>
          <Text style={{ color: '#FFFFFF', fontSize: 24, fontWeight: 'bold' }}>➔</Text>
        </TouchableOpacity>

        {/* Stats Row */}
        <View style={styles.statsRow}>
          <StatCard
            title="Today Meetings"
            value={meetings.length}
            icon="📅"
            accentColor={COLORS.primary}
          />
          <StatCard
            title="Pending Approval"
            value={pendingCount}
            icon="⏳"
            accentColor={COLORS.warning}
          />
        </View>

        {/* Today's Schedule Overview */}
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.textPrimary }]}>
            Today's Visitors List
          </Text>
          <TouchableOpacity onPress={() => navigation.navigate('AvailableSchedules')}>
            <Text style={styles.linkText}>View Master Schedule →</Text>
          </TouchableOpacity>
        </View>

        {meetings.length === 0 ? (
          <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, alignItems: 'center', marginVertical: 8 }]}>
            <Text style={{ fontSize: 32, marginBottom: 8 }}>📋</Text>
            <Text style={[styles.emptyTitle, { color: theme.textPrimary }]}>
              No meetings scheduled for today
            </Text>
            <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
              Click 'Create New Meeting' to schedule a visitor meeting with Master Admin approval.
            </Text>
          </View>
        ) : (
          meetings.map((meeting) => (
            <TouchableOpacity
              key={meeting.id}
              onPress={() => navigation.navigate('MeetingDetails', { id: meeting.id })}
              style={[styles.meetingCard, { backgroundColor: theme.cardBg, borderColor: theme.border }]}
            >
              <View style={{ flex: 1, marginRight: 10 }}>
                <Text style={[styles.visitorName, { color: theme.textPrimary }]}>
                  {meeting.visitorName}
                </Text>
                {meeting.visitorCompany && (
                  <Text style={[styles.companyName, { color: theme.textSecondary }]}>
                    {meeting.visitorCompany}
                  </Text>
                )}
                <Text style={[styles.slotText, { color: theme.textSecondary }]}>
                  ⏰ Slot: <Text style={{ color: COLORS.primary, fontWeight: '700' }}>{meeting.meetingTime}</Text>
                </Text>
                <Text style={[styles.purposeText, { color: theme.textSecondary }]} numberOfLines={1}>
                  Purpose: {meeting.meetingPurpose}
                </Text>
              </View>

              <StatusBadge status={meeting.status} />
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  actionBanner: {
    backgroundColor: COLORS.primary,
    padding: 18,
    borderRadius: 20,
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    shadowColor: COLORS.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
  actionTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '800',
  },
  actionSubtitle: {
    color: '#DBEAFE',
    fontSize: 11,
    marginTop: 2,
    fontWeight: '500',
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '800',
  },
  linkText: {
    color: COLORS.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '700',
  },
  emptySubtitle: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 4,
  },
  meetingCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  visitorName: {
    fontSize: 15,
    fontWeight: '800',
  },
  companyName: {
    fontSize: 11,
    marginBottom: 4,
  },
  slotText: {
    fontSize: 11,
  },
  purposeText: {
    fontSize: 11,
    marginTop: 2,
  },
});
